import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import MeshGradient from '../../components/ui/MeshGradient';
import NoiseTexture from '../../components/ui/NoiseTexture';
import './Auth.css';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('request'); // 'request' or 'reset'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { clientId } = window.CONFIG.cognito;

      const response = await fetch(`https://cognito-idp.eu-west-2.amazonaws.com/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.ForgotPassword',
        },
        body: JSON.stringify({
          ClientId: clientId,
          Username: email,
        }),
      });

      const data = await response.json();

      if (data.CodeDeliveryDetails) {
        setStep('reset');
      } else if (data.__type?.includes('UserNotFoundException')) {
        setError('No account found with this email');
      } else if (data.__type?.includes('LimitExceededException')) {
        setError('Too many attempts. Please try again later.');
      } else {
        setError(data.message || 'Failed to send reset code.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      const { clientId } = window.CONFIG.cognito;

      const response = await fetch(`https://cognito-idp.eu-west-2.amazonaws.com/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.ConfirmForgotPassword',
        },
        body: JSON.stringify({
          ClientId: clientId,
          Username: email,
          ConfirmationCode: code,
          Password: newPassword,
        }),
      });

      const data = await response.json();

      if (!data.__type) {
        navigate('/auth/login', { state: { passwordReset: true } });
      } else if (data.__type?.includes('CodeMismatchException')) {
        setError('Invalid verification code');
      } else if (data.__type?.includes('ExpiredCodeException')) {
        setError('Code has expired. Please request a new one.');
        setStep('request');
      } else if (data.__type?.includes('InvalidPasswordException')) {
        setError('Password must include uppercase, lowercase, numbers, and special characters');
      } else {
        setError(data.message || 'Failed to reset password.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <MeshGradient />
      <NoiseTexture />
      <div className="auth-card">
        <div className="auth-header">
          <img 
            src={`${process.env.PUBLIC_URL}/images/fokusLogo.svg`} 
            alt="Fokus Logo" 
            className="auth-logo-image"
          />
          <h1 className="auth-title">
            {step === 'request' ? 'Reset your password' : 'Create new password'}
          </h1>
          <p className="auth-tagline">
            {step === 'request' 
              ? "Enter your email and we'll send you a reset code" 
              : 'Enter the code we sent you'}
          </p>
        </div>

        {step === 'request' ? (
          <form onSubmit={handleRequestCode} className="auth-form">
            {error && <div className="auth-error">{error}</div>}

            <div className="auth-field">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                autoFocus
              />
            </div>

            <button type="submit" className="auth-button auth-button--primary" disabled={loading}>
              {loading ? 'Sending...' : 'Send reset code'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleResetPassword} className="auth-form">
            {error && <div className="auth-error">{error}</div>}
            
            <div className="auth-email-display">
              <span>{email}</span>
              <button 
                type="button" 
                className="auth-link-button"
                onClick={() => setStep('request')}
              >
                Change
              </button>
            </div>

            <div className="auth-field">
              <label htmlFor="code">Verification code</label>
              <input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
                required
                autoFocus
                maxLength={6}
                className="auth-code-input"
              />
            </div>

            <div className="auth-field">
              <label htmlFor="newPassword">New password</label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
              <span className="auth-hint">Min 8 characters with uppercase, lowercase & numbers</span>
            </div>

            <div className="auth-field">
              <label htmlFor="confirmPassword">Confirm password</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>

            <button type="submit" className="auth-button auth-button--primary" disabled={loading}>
              {loading ? 'Resetting...' : 'Reset password'}
            </button>
          </form>
        )}

        <div className="auth-footer">
          <Link to="/auth/login" className="auth-link">← Back to login</Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
